import { useMutation } from "@tanstack/react-query";
import type { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../contexts/analytics/hooks";
import { useHospitalData } from "../contexts/HospitalDataProvider";
import { useSnackbar } from "../contexts/SnackbarContext";
import { backendService } from "../services/backend.service";
import { hasHrisIntegrationOfType } from "../utils/hrisIntegrations";

type SyncEmployeesParams = { startDate?: DateTime; endDate?: DateTime };

export const useSyncEmployees = () => {
	const { showErrorSnackbar, chosenDepartment, refreshNursesData } = useHospitalData();
	const { showSnackbar } = useSnackbar();
	const { track } = useAnalytics();
	const { t } = useTranslation();

	return useMutation({
		mutationFn: async (params: SyncEmployeesParams) => {
			if (!hasHrisIntegrationOfType(chosenDepartment, "employees") || !chosenDepartment?.department_id) return;
			const response = await backendService.syncEmployees({ ...params, departmentId: chosenDepartment.department_id });
			return response.data;
		},
		onSuccess: async (data) => {
			await refreshNursesData(chosenDepartment.department_id);
			showSnackbar(t("employee.sync.completed"), "success");
			track("Integrations - Employees synced", {
				inserted: data?.to_insert.length,
				updated: data?.to_update.length,
				deleted: data?.to_delete.length,
			});
		},
		onError: (error) => showErrorSnackbar(t("employee.sync.failed"), { action: "external_employee_sync" }, error),
	});
};
